/*
 * Mobile sticky add-to-cart for the Hydraburst PDP.
 * Mirrors the state that hydraburst-product-section publishes ('hb:product-updated') and proxies
 * every interaction to that section's own controls, so pricing and cart logic live in one place.
 */
class HbStickyAtc extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.hb-product');
    this.form = this.section && this.section.querySelector('.hb-product__form');
    if (!this.form) return;

    this.mainAtc = this.form.querySelector('[data-hb-atc]');
    this.mainAtcText = this.form.querySelector('[data-hb-atc-text]');
    this.panel = this.querySelector('[data-hb-sticky-panel]');
    this.bar = this.querySelector('[data-hb-sticky-bar]');
    this.editButton = this.querySelector('[data-hb-sticky-edit]');
    this.atcButton = this.querySelector('[data-hb-sticky-atc]');
    this.atcText = this.querySelector('[data-hb-sticky-atc-text]');
    this.image = this.querySelector('[data-hb-sticky-image]');

    this.mobileQuery = window.matchMedia('(max-width: 799px)');
    this.pastForm = false;

    this.onUpdate = (event) => this.render(event.detail);
    this.onVisibilityChange = () => this.updateVisibility();
    this.onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.classList.contains('is-expanded')) return;
      this.setExpanded(false);
      this.editButton.focus();
    };
    this.onPointerdown = (event) => {
      if (this.classList.contains('is-expanded') && !this.contains(event.target)) {
        this.setExpanded(false);
      }
    };

    this.section.addEventListener('hb:product-updated', this.onUpdate);
    this.mobileQuery.addEventListener('change', this.onVisibilityChange);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onPointerdown);

    this.editButton.addEventListener('click', () => {
      this.setExpanded(this.panel.hidden);
    });

    this.panel.addEventListener('click', (event) => {
      const flavor = event.target.closest('[data-hb-sticky-flavor]');
      const purchase = event.target.closest('[data-hb-sticky-purchase]');

      if (flavor) {
        this.proxyClick(
          this.section.querySelector(`[data-hb-flavor-product="${flavor.dataset.hbStickyFlavor}"]`)
        );
      } else if (purchase) {
        this.proxyClick(
          this.section.querySelector(`[data-hb-purchase="${purchase.dataset.hbStickyPurchase}"]`)
        );
      }
    });

    // Reuse the section's submit handler so the cart drawer flow stays identical
    this.atcButton.addEventListener('click', () => {
      if (!this.mainAtc || this.atcButton.disabled) return;
      if (this.atcButton.getAttribute('aria-disabled') === 'true') return;
      this.mainAtc.click();
    });

    // The section is done once the user scrolls past the whole product form
    this.formObserver = new IntersectionObserver(([entry]) => {
      this.pastForm = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.updateVisibility();
    });
    this.formObserver.observe(this.form);

    // Mirror the main button label and state (price, "Adding...", "Sold Out", errors)
    if (this.mainAtc) {
      this.atcObserver = new MutationObserver(() => this.syncAtc());
      this.atcObserver.observe(this.mainAtc, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
      this.syncAtc();
    }

    // The section may have rendered before this element was upgraded
    if (this.section.hbState) this.render(this.section.hbState);
  }

  disconnectedCallback() {
    if (this.section) this.section.removeEventListener('hb:product-updated', this.onUpdate);
    if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onVisibilityChange);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onPointerdown);
    if (this.formObserver) this.formObserver.disconnect();
    if (this.atcObserver) this.atcObserver.disconnect();
    document.documentElement.classList.remove('hb-sticky-atc-active');
  }

  updateVisibility() {
    const visible = this.pastForm && this.mobileQuery.matches;

    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    if (!visible) this.setExpanded(false);

    document.documentElement.classList.toggle('hb-sticky-atc-active', visible);
    if (visible) {
      document.documentElement.style.setProperty(
        '--hb-sticky-atc-offset',
        `${this.bar.offsetHeight}px`
      );
    }
  }

  setExpanded(expanded) {
    this.panel.hidden = !expanded;
    this.editButton.setAttribute('aria-expanded', String(expanded));
    this.classList.toggle('is-expanded', expanded);
  }

  // Click a section control without moving the page. The purchase cards are <label>s wrapping a
  // hidden radio, and activating that radio focuses it, which scrolls the page up to it.
  proxyClick(target) {
    if (!target) return;

    const { scrollX, scrollY } = window;
    const cancelActivation = (event) => event.preventDefault();

    target.addEventListener('click', cancelActivation, { once: true });
    target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    target.removeEventListener('click', cancelActivation);

    if (window.scrollY !== scrollY || window.scrollX !== scrollX) {
      window.scrollTo(scrollX, scrollY);
    }
  }

  syncAtc() {
    this.atcText.textContent = this.mainAtcText ? this.mainAtcText.textContent.trim() : '';
    this.atcButton.disabled = this.mainAtc.disabled;
    this.atcButton.setAttribute(
      'aria-disabled',
      this.mainAtc.getAttribute('aria-disabled') === 'true' ? 'true' : 'false'
    );
  }

  setText(selector, text) {
    const element = this.querySelector(selector);
    if (element) element.textContent = text;
  }

  // Text the section already computed (per-day price, subscribe copy)
  sectionText(selector) {
    const element = this.section.querySelector(selector);
    return element ? element.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  render({ productKey, product, variant, purchaseType, isSubscribe }) {
    const flavor = product.flavor_label || product.title || '';
    const hasPlan = Boolean(variant.selling_plan && variant.selling_plan_price_money);

    this.setText('[data-hb-sticky-flavor-label]', flavor);
    this.setText(
      '[data-hb-sticky-delivery]',
      isSubscribe
        ? this.capitalize(variant.selling_plan_name) || 'Subscription'
        : this.dataset.oneTimeText || ''
    );

    const nextImage = variant.image || product.featured_image;
    if (this.image && nextImage) {
      // The section ships 1200px images; the thumbnail only needs a small one
      this.image.src = nextImage.replace(/width=\d+/, 'width=240');
      this.image.alt = flavor;
    }

    this.querySelectorAll('[data-hb-sticky-flavor]').forEach((button) => {
      const active = button.dataset.hbStickyFlavor === productKey;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    this.querySelectorAll('[data-hb-sticky-purchase]').forEach((button) => {
      const type = button.dataset.hbStickyPurchase;
      const active = type === purchaseType && (type !== 'subscribe' || hasPlan);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      if (type === 'subscribe') button.disabled = !hasPlan;
    });

    this.setText('[data-hb-sticky-sub-price]', hasPlan ? variant.selling_plan_price_money : '—');
    this.setText('[data-hb-sticky-sub-compare]', hasPlan ? variant.price_money : '');
    this.setText('[data-hb-sticky-sub-perday]', this.sectionText('[data-hb-subscribe-per-day]'));
    this.setText('[data-hb-sticky-sub-frequency]', this.sectionText('[data-hb-subscribe-frequency]'));
    this.setText('[data-hb-sticky-sub-subtext]', this.sectionText('[data-hb-subscribe-subtext]'));
    this.setText('[data-hb-sticky-onetime-price]', variant.price_money);
    this.setText('[data-hb-sticky-onetime-perday]', this.sectionText('[data-hb-onetime-per-day]'));

    this.syncAtc();
  }

  capitalize(value) {
    const text = String(value || '').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

if (!customElements.get('hb-sticky-atc')) {
  customElements.define('hb-sticky-atc', HbStickyAtc);
}
